"use client";

import { useIsMobile } from "@/utils/useIsMobile";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

const LETTER_SPACING = "-0.03em";
// How many px of drag/wheel movement roughly equals one index step.
// Tune this if the drag feels too fast/slow.
const PIXELS_PER_STEP = 80;

// Center -> outward style stops (distance measured in index-steps from the center row).
// height doubles as the actual row height now, not just line-height.
const STYLE_STOPS = [
  { height: 84, fontSize: 56, fontWeight: 700, color: "#003D6C" },
  { height: 55, fontSize: 30, fontWeight: 600, color: "#4B5563" },
  { height: 37, fontSize: 18, fontWeight: 500, color: "#94A3B8" },
];

// Smaller, tighter stops used on mobile viewports (row height = fontSize + ~6px gap).
const MOBILE_STYLE_STOPS = [
  { height: 64, fontSize: 42, fontWeight: 700, color: "#003D6C" },
  { height: 46, fontSize: 26, fontWeight: 600, color: "#4B5563" },
  { height: 38, fontSize: 16, fontWeight: 500, color: "#94A3B8" },
];

function containerHeightFor(stops: typeof STYLE_STOPS) {
  return stops[0].height + 2 * stops[1].height + 2 * stops[2].height;
}

function hexToRgb(hex: string) {
  const n = parseInt(hex.replace("#", ""), 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

function lerpColor(from: string, to: string, t: number) {
  const a = hexToRgb(from);
  const b = hexToRgb(to);
  return `rgb(${Math.round(lerp(a.r, b.r, t))}, ${Math.round(lerp(a.g, b.g, t))}, ${Math.round(lerp(a.b, b.b, t))})`;
}

// Interpolated style at a given ABSOLUTE distance (>= 0) from the center row.
function getItemStyle(distance: number, stops: typeof STYLE_STOPS) {
  const clamped = Math.min(stops.length - 1, Math.max(0, distance));
  const lower = Math.floor(clamped);
  const upper = Math.min(stops.length - 1, Math.ceil(clamped));
  const t = clamped - lower;
  const from = stops[lower];
  const to = stops[upper];
  return {
    height: lerp(from.height, to.height, t),
    fontSize: lerp(from.fontSize, to.fontSize, t),
    fontWeight: Math.round(lerp(from.fontWeight, to.fontWeight, t)),
    color: lerpColor(from.color, to.color, t),
  };
}

// G(x) = integral from 0 to x of height(t) dt, for x >= 0.
// This gives the exact, gap-free distance (in px) between the center row
// and a row `x` steps away, given the piecewise-linear height function above.
function G(x: number, stops: typeof STYLE_STOPS) {
  const [s0, s1, s2] = stops;
  if (x <= 0) return 0;
  if (x <= 1) {
    return s0.height * x + ((s1.height - s0.height) * (x * x)) / 2;
  }
  const g1 = s0.height + (s1.height - s0.height) * 0.5; // G(1)
  if (x <= 2) {
    const dx = x - 1;
    return g1 + s1.height * dx + ((s2.height - s1.height) * (dx * dx)) / 2;
  }
  const g2 = g1 + s1.height + (s2.height - s1.height) * 0.5; // G(2)
  return g2 + s2.height * (x - 2);
}

// Signed offset (px) from the center row to a row `distance` steps away (can be negative).
function offsetFor(distance: number, stops: typeof STYLE_STOPS) {
  return distance >= 0 ? G(distance, stops) : -G(-distance, stops);
}

export default function RetirementDurationWheel({
  value,
  min,
  max,
  onChangeAction,
  unitLabel = "Years",
}: {
  value: number;
  min: number;
  max: number;
  onChangeAction: (v: number) => void;
  unitLabel?: string;
}) {
  // Mobile View Variable
  const isMob = useIsMobile(631);

  // Latest-value refs so long-running rAF loops never call stale props.
  const valueRef = useRef(value);
  const minRef = useRef(min);
  const onChangeActionRef = useRef(onChangeAction);
  valueRef.current = value;
  minRef.current = min;
  onChangeActionRef.current = onChangeAction;

  // centerIndexRef is the single source of truth for position; `centerIndex` state
  // just triggers re-renders. We never use the functional setState form, so no
  // side effects (like calling onChangeAction) ever run inside a state updater.
  const centerIndexRef = useRef(value - min);
  const [centerIndex, setCenterIndexState] = useState(centerIndexRef.current);

  const setIndex = useCallback((next: number) => {
    centerIndexRef.current = next;
    setCenterIndexState(next);
  }, []);

  const dragState = useRef<{ dragging: boolean; startY: number; startIndex: number } | null>(null);
  const settleTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = useRef<number | null>(null);
  const wheelTarget = useRef(value - min);
  const velocityRef = useRef(0); // index-units per ms, tracked while dragging
  const lastMoveRef = useRef<{ y: number; t: number } | null>(null);

  const count = max - min + 1;
  const clampIndex = useCallback((i: number) => Math.min(count - 1, Math.max(0, i)), [count]);

  const stopAnim = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  // Runs `step` on every frame until it returns false. Only one loop is ever
  // active (previous loop is always cancelled first), so wheel/drag/snap
  // animations can never fight each other and cause a sudden jump.
  const runAnimation = useCallback(
    (step: () => boolean) => {
      stopAnim();
      const loop = () => {
        const shouldContinue = step();
        rafRef.current = shouldContinue ? requestAnimationFrame(loop) : null;
      };
      rafRef.current = requestAnimationFrame(loop);
    },
    [stopAnim],
  );

  // Smoothly animate to a fixed target index. `commit` fires onChangeAction once
  // settled — as an ordinary statement inside the rAF callback, never inside setState.
  const snapTo = useCallback(
    (target: number, commit: boolean) => {
      wheelTarget.current = target;
      runAnimation(() => {
        const current = centerIndexRef.current;
        const next = current + (target - current) * 0.25;
        if (Math.abs(target - next) < 0.01) {
          setIndex(target);
          if (commit) {
            const finalValue = minRef.current + target;
            if (finalValue !== valueRef.current) onChangeActionRef.current(finalValue);
          }
          return false;
        }
        setIndex(next);
        return true;
      });
    },
    [runAnimation, setIndex],
  );

  // Keep in sync if `value` changes externally (e.g. another control updates it).
  useLayoutEffect(() => {
    snapTo(clampIndex(value - min), false);
  }, [value, min]);

  useEffect(() => {
    return () => {
      stopAnim();
      if (settleTimeout.current) clearTimeout(settleTimeout.current);
    };
  }, [stopAnim]);

  const scheduleSnap = useCallback(() => {
    if (settleTimeout.current) clearTimeout(settleTimeout.current);
    settleTimeout.current = setTimeout(() => {
      const target = clampIndex(Math.round(wheelTarget.current));
      snapTo(target, true);
    }, 120);
  }, [clampIndex, snapTo]);

  // Continuously chases the (possibly moving) wheelTarget — this is what smooths
  // out chunky mouse-wheel notches and fast trackpad flicks.
  const runWheelChase = useCallback(() => {
    runAnimation(() => {
      const current = centerIndexRef.current;
      const target = wheelTarget.current;
      const next = current + (target - current) * 0.18;
      setIndex(next);
      return Math.abs(target - next) > 0.002;
    });
  }, [runAnimation, setIndex]);

  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      e.preventDefault();
      wheelTarget.current = clampIndex(wheelTarget.current + e.deltaY / PIXELS_PER_STEP);
      runWheelChase();
      scheduleSnap();
    },
    [clampIndex, runWheelChase, scheduleSnap],
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      (e.target as Element).setPointerCapture?.(e.pointerId);
      if (settleTimeout.current) clearTimeout(settleTimeout.current);
      stopAnim();
      velocityRef.current = 0;
      lastMoveRef.current = { y: e.clientY, t: performance.now() };
      dragState.current = { dragging: true, startY: e.clientY, startIndex: centerIndexRef.current };
    },
    [stopAnim],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragState.current?.dragging) return;
      const delta = (dragState.current.startY - e.clientY) / PIXELS_PER_STEP;
      const next = clampIndex(dragState.current.startIndex + delta);
      setIndex(next);
      wheelTarget.current = next;

      // Track short-term velocity (index-units per ms) for a release fling.
      const now = performance.now();
      const last = lastMoveRef.current;
      if (last) {
        const dt = now - last.t;
        if (dt > 0) {
          const deltaIndex = (last.y - e.clientY) / PIXELS_PER_STEP;
          const instVelocity = deltaIndex / dt;
          velocityRef.current = lerp(velocityRef.current, instVelocity, 0.5);
        }
      }
      lastMoveRef.current = { y: e.clientY, t: now };
    },
    [clampIndex, setIndex],
  );

  const endDrag = useCallback(() => {
    if (!dragState.current?.dragging) return;
    dragState.current.dragging = false;
    lastMoveRef.current = null;

    const FRAME_MS = 16;
    let velocity = velocityRef.current * FRAME_MS; // index-units per frame
    velocityRef.current = 0;

    // Small flicks just snap normally; bigger flicks carry momentum before snapping.
    if (Math.abs(velocity) < 0.02) {
      snapTo(clampIndex(Math.round(centerIndexRef.current)), true);
      return;
    }

    runAnimation(() => {
      const current = centerIndexRef.current;
      const next = current + velocity;
      velocity *= 0.92; // friction
      const clamped = clampIndex(next);
      const hitEdge = clamped !== next;
      setIndex(clamped);
      if (hitEdge || Math.abs(velocity) < 0.02) {
        snapTo(clampIndex(Math.round(clamped)), true);
        return false;
      }
      return true;
    });
  }, [clampIndex, runAnimation, setIndex, snapTo]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowUp") {
      e.preventDefault();
      snapTo(clampIndex(Math.round(centerIndexRef.current) - 1), true);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      snapTo(clampIndex(Math.round(centerIndexRef.current) + 1), true);
    }
  };

  const styleStops = isMob ? MOBILE_STYLE_STOPS : STYLE_STOPS;
  const containerHeight = containerHeightFor(styleStops);
  const centerY = containerHeight / 2;
  const RANGE = 3; // render a little past distance 2 so the fade mask has something to fade
  const startIdx = clampIndex(Math.floor(centerIndex - RANGE));
  const endIdx = clampIndex(Math.ceil(centerIndex + RANGE));

  const items = [];
  for (let i = startIdx; i <= endIdx; i++) {
    const distance = i - centerIndex;
    const style = getItemStyle(Math.abs(distance), styleStops);
    const top = centerY + offsetFor(distance, styleStops) - style.height / 2;
    items.push({ index: i, year: min + i, style, top });
  }

  return (
    <div className="relative m-auto w-full" style={{ height: containerHeight }}>
      {/* Fade masks top/bottom so the wheel feels continuous */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-2 h-full bg-linear-to-b/srgb from-white/70 via-transparent to-white/70" />

      {/* Highlighted center band — always exactly the center-row height */}
      <div
        className="pointer-events-none absolute inset-x-0 z-1 rounded-4xl border border-[#003D6C] bg-[#003d6c11]"
        style={{ top: centerY - styleStops[0].height / 2, height: styleStops[0].height }}
      >
        {/* Unit label stays fixed and fully visible regardless of scroll position */}

        <span className="pointer-events-none inline-block w-fit h-fit select-none text-3xl xs:text-[40px] font-medium text-[#91A1AD] tracking-[-3%] absolute inset-0 m-auto translate-x-full">
          {unitLabel}
        </span>
      </div>

      <div className="relative flex h-full items-center justify-center">
        {/* Draggable numbers column - fixed width so it never shifts as digit count changes */}
        <div
          className="relative w-full h-full touch-none select-none outline-hidden overflow-hidden"
          tabIndex={0}
          role="slider"
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={value}
          aria-label={`Retirement duration in ${unitLabel.toLowerCase()}`}
          onWheel={handleWheel}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onKeyDown={handleKeyDown}
        >
          {items.map((it) => (
            <button
              key={it.index}
              type="button"
              onClick={() => snapTo(it.index, true)}
              className={`absolute left-0 right-0 flex items-center justify-center ${isMob ? "pr-1" : "pr-2"}`}
              style={{ top: it.top, height: it.style.height }}
            >
              <span
                style={{
                  fontSize: it.style.fontSize,
                  lineHeight: `${it.style.height}px`,
                  fontWeight: it.style.fontWeight,
                  color: it.style.color,
                  letterSpacing: LETTER_SPACING,
                }}
              >
                {it.year}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
